import { useState } from "react";
import ApiDashboard from "../../components/integrations/ApiDashboard";
import { SAMPLE_WEBHOOKS, MARKETING_CRM, STORAGE_PUBLISHING } from "../../lib/sample-data";
import { useRole } from "../../contexts/RoleContext";
import {
  User, Gift, CreditCard, BarChart3, Ticket, Share2, KeyRound, Webhook, Plug,
  Receipt, Wallet, Building2, Users, Palette, Database, Lock, Bell, LifeBuoy,
  MessageCircle, Mail, Check, Copy, Plus, Trash2, ShieldCheck,
} from "lucide-react";
import { cn } from "../../lib/utils";

const Eyebrow = ({ children }) => (
  <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-aqua)]">{children}</div>
);

const Card = ({ title, eyebrow, children, action }) => (
  <div className="rounded-2xl p-5 border border-[var(--pl-border)] bg-[var(--pl-surface)]/60">
    {(eyebrow || action) && (
      <div className="flex items-start justify-between gap-2 mb-2">
        {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
        {action}
      </div>
    )}
    {title && <div className="font-display text-base font-medium text-[var(--pl-text)]">{title}</div>}
    {children && <div className="mt-2">{children}</div>}
  </div>
);

const Field = ({ label, children }) => (
  <label className="block space-y-1.5">
    <div className="text-[11px] uppercase tracking-[0.18em] text-[var(--pl-text-muted)]">{label}</div>
    {children}
  </label>
);

const Input = ({ testId, ...rest }) => (
  <input
    data-testid={testId}
    style={{ background: "color-mix(in srgb, var(--pl-surface) 70%, transparent)", color: "var(--pl-text)" }}
    className="w-full rounded-xl border border-[var(--pl-border)] px-3 h-11 text-sm focus:outline-none focus:border-[var(--pl-aqua)]/50"
    {...rest}
  />
);

const SETTINGS_NAV = [
  {
    section: "Account settings",
    items: [
      { id: "profile", label: "Personal Profile", icon: User, to: "/settings/profile" },
      { id: "gifts", label: "Gifts", icon: Gift, to: "/settings/gifts" },
      { id: "subscription", label: "Subscription", icon: CreditCard, to: "/settings/subscription" },
      { id: "usage", label: "Usage", icon: BarChart3, to: "/settings/usage" },
      { id: "promo", label: "Promo Code", icon: Ticket, to: "/settings/promo-code" },
      { id: "affiliate", label: "Affiliate Portal", icon: Share2, to: "/settings/affiliate" },
      { id: "api", label: "API Dashboard", icon: KeyRound, to: "/settings/api" },
      { id: "webhooks", label: "Webhooks", icon: Webhook, to: "/settings/webhooks" },
      { id: "connected", label: "Connected Apps", icon: Plug, to: "/settings/connected-apps" },
      { id: "billing", label: "Billing History", icon: Receipt, to: "/settings/billing" },
      { id: "payment", label: "Payment Methods", icon: Wallet, to: "/settings/payment-methods" },
    ],
  },
  {
    section: "Workspace settings",
    items: [
      { id: "workspace", label: "Workspace Profile", icon: Building2, to: "/settings/workspace" },
      { id: "team", label: "Team Members", icon: Users, to: "/settings/team" },
      { id: "brand", label: "Brand Defaults", icon: Palette, to: "/settings/brand-defaults" },
      { id: "storage", label: "Storage", icon: Database, to: "/settings/storage" },
      { id: "privacy", label: "Privacy", icon: Lock, to: "/settings/privacy" },
      { id: "notifications", label: "Notifications", icon: Bell, to: "/settings/notifications" },
    ],
  },
  {
    section: "Support",
    items: [
      { id: "support", label: "Help Center · Community · Contact", icon: LifeBuoy, to: "/settings/support" },
    ],
  },
];

export { SETTINGS_NAV };

// --- 1. Personal Profile ---
export function PersonalProfile() {
  return (
    <div data-testid="settings-profile" className="space-y-6">
      <Card eyebrow="Profile">
        <div className="flex items-center gap-4">
          <div className="h-16 w-16 rounded-2xl pl-signature-gradient grid place-items-center text-[#061414] font-bold text-xl">PL</div>
          <div className="flex-1">
            <div className="font-display text-xl font-semibold text-[var(--pl-text)]">modularfounder · Personal</div>
            <div className="text-xs text-[var(--pl-text-muted)] mt-1">Free · Demo workspace</div>
          </div>
          <button data-testid="profile-change-avatar" className="h-9 px-3 rounded-xl border border-[var(--pl-border)] text-xs text-[var(--pl-text)] hover:border-[var(--pl-aqua)]/40">
            Change avatar
          </button>
        </div>
      </Card>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card><Field label="Full name"><Input testId="profile-name" defaultValue="Founder" /></Field></Card>
        <Card><Field label="Email"><Input testId="profile-email" defaultValue="founder@premierlab.ai" /></Field></Card>
        <Card><Field label="Workspace name"><Input testId="profile-workspace" defaultValue="Personal Workspace" /></Field></Card>
        <Card>
          <Field label="Account type">
            <div className="flex items-center gap-2 h-11 px-3 rounded-xl border border-[var(--pl-border)] bg-[var(--pl-surface)]/50">
              <ShieldCheck className="h-4 w-4 text-[var(--pl-aqua)]" />
              <span className="text-sm text-[var(--pl-text)]">Personal · Demo</span>
            </div>
          </Field>
        </Card>
      </div>
      <div className="flex justify-end">
        <button data-testid="profile-save-btn" className="h-11 px-5 rounded-xl pl-btn-aqua text-sm">Save changes</button>
      </div>
    </div>
  );
}

// --- 2. Gifts ---
export function Gifts() {
  const cards = [
    { label: "Send credits", desc: "Gift PremierLab credits to a friend.", icon: Gift },
    { label: "Gift subscription", desc: "Gift a 1-month or 12-month plan.", icon: CreditCard },
    { label: "Bulk gift", desc: "Reward your team or community.", icon: Users },
  ];
  return (
    <div data-testid="settings-gifts" className="space-y-6">
      <Card eyebrow="Gifts">
        <p className="text-sm text-[var(--pl-text-muted)] mt-1">Send PremierLab credits or subscriptions to anyone.</p>
      </Card>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {cards.map((c) => {
          const I = c.icon;
          return (
            <div key={c.label} data-testid={`gift-${c.label}`} className="rounded-2xl p-5 border border-[var(--pl-border)] bg-[var(--pl-surface)]/60 pl-lift">
              <div className="h-10 w-10 rounded-xl bg-[var(--pl-aqua)]/10 border border-[var(--pl-aqua)]/30 grid place-items-center"><I className="h-5 w-5 text-[var(--pl-aqua)]" /></div>
              <div className="font-medium text-[var(--pl-text)] mt-3">{c.label}</div>
              <div className="text-xs text-[var(--pl-text-muted)] mt-1">{c.desc}</div>
              <button data-testid={`gift-btn-${c.label}`} className="mt-4 h-9 px-3 rounded-xl pl-btn-aqua text-xs">Open</button>
            </div>
          );
        })}
      </div>
      <Card eyebrow="Gift history">
        <div className="text-sm text-[var(--pl-text-muted)]">No gifts sent yet.</div>
      </Card>
    </div>
  );
}

// --- 3. Subscription ---
export function Subscription() {
  return (
    <div data-testid="settings-subscription" className="space-y-6">
      <div className="rounded-2xl p-6 border border-[var(--pl-border)] bg-[var(--pl-surface)]/60 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <Eyebrow>Current plan</Eyebrow>
          <div className="font-display text-2xl font-semibold text-[var(--pl-text)] mt-1 flex items-center gap-2">
            Premier · $129/mo
            <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-md border border-emerald-500/40 text-emerald-300">Active</span>
          </div>
          <div className="text-xs text-[var(--pl-text-muted)] mt-1">Renews March 1, 2026</div>
        </div>
        <button data-testid="sub-upgrade-btn" className="h-11 px-4 rounded-xl pl-btn-lime text-sm">Upgrade · Update payment</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card eyebrow="Credits">
          <div className="flex items-baseline gap-2 mt-1">
            <div className="font-display text-3xl font-semibold text-[var(--pl-text)]">38,750</div>
            <div className="text-xs text-[var(--pl-text-muted)]">of 50,000 monthly</div>
          </div>
          <div className="mt-3 h-1.5 rounded-full bg-white/5 overflow-hidden">
            <div className="h-full pl-signature-gradient" style={{ width: "77%" }} />
          </div>
        </Card>
        <Card eyebrow="Auto-refill">
          <div className="flex items-center justify-between mt-1">
            <div>
              <div className="text-sm text-[var(--pl-text)]">Auto-refill is disabled</div>
              <div className="text-xs text-[var(--pl-text-muted)] mt-0.5">Top up automatically when credits run low.</div>
            </div>
            <button data-testid="sub-autorefill-btn" className="h-9 px-3 rounded-xl pl-btn-aqua text-xs">Set up</button>
          </div>
        </Card>
      </div>

      <Card eyebrow="Plan benefits">
        <ul className="mt-2 space-y-1.5 text-sm">
          {[
            "50,000 AI credits per month",
            "Access to GPT-5.2, Claude Sonnet 4.5, Gemini 3, Nano Banana, ElevenLabs",
            "Concurrent: up to 4 videos, 8 images, 3 influencers",
            "Premium templates and brand kits",
            "Priority support and early access",
          ].map((b) => (
            <li key={b} className="flex items-center gap-2 text-[var(--pl-text)]">
              <Check className="h-4 w-4 text-emerald-400" /> {b}
            </li>
          ))}
        </ul>
      </Card>

      <Card eyebrow="Active model access">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
          {["GPT-5.2", "Claude Sonnet 4.5", "Gemini 3 Pro", "Nano Banana", "ElevenLabs v3", "Whisper", "Higgsfield (BYO)", "HeyGen (BYO)"].map((m) => (
            <div key={m} className="rounded-xl px-3 py-2 text-xs border border-[var(--pl-border)] bg-white/[0.03] text-[var(--pl-text)]">
              {m}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

// --- 4. Usage ---
export function Usage() {
  const rows = [
    ["May 22 · 10:24", "Nano Banana", "Image gen", 2],
    ["May 22 · 09:18", "GPT-5.2", "Slide outline", 6],
    ["May 22 · 08:02", "Claude Sonnet", "Chapter draft", 18],
    ["May 21 · 22:11", "ElevenLabs", "Voiceover", 4],
    ["May 21 · 19:48", "Nano Banana", "Cover", 2],
  ];
  return (
    <div data-testid="settings-usage" className="space-y-6">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <Eyebrow>Usage history</Eyebrow>
          <div className="font-display text-xl text-[var(--pl-text)] mt-1">Credits, generations, and cost</div>
        </div>
        <div className="flex gap-2">
          <select
            data-testid="usage-range"
            style={{ background: "color-mix(in srgb, var(--pl-surface) 70%, transparent)", color: "var(--pl-text)" }}
            className="h-10 px-3 rounded-xl border border-[var(--pl-border)] text-xs"
          >
            <option>Last 7 days</option>
            <option>Last 30 days</option>
            <option>This cycle</option>
          </select>
          <button data-testid="usage-buyboost-btn" className="h-10 px-3 rounded-xl pl-btn-lime text-xs">Buy boost credits</button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Credits used" value="11,260" />
        <Stat label="Generations" value="184" />
        <Stat label="Features used" value="12" />
        <Stat label="Cost estimate" value="$42.30" />
      </div>

      <Card eyebrow="By provider">
        <div className="mt-2 space-y-2 text-sm">
          {[
            ["GPT-5.2", 48], ["Claude Sonnet", 32], ["Nano Banana", 12], ["ElevenLabs", 8],
          ].map(([k, pct]) => (
            <div key={k}>
              <div className="flex justify-between text-[var(--pl-text)]"><span>{k}</span><span className="font-mono text-xs text-[var(--pl-text-muted)]">{pct}%</span></div>
              <div className="mt-1 h-1.5 rounded-full bg-white/5 overflow-hidden"><div className="h-full pl-signature-gradient" style={{ width: `${pct}%` }} /></div>
            </div>
          ))}
        </div>
      </Card>

      <Card eyebrow="History">
        <div className="overflow-x-auto">
          <table className="w-full text-sm mt-2">
            <thead className="text-[10px] uppercase tracking-[0.18em] text-[var(--pl-text-muted)]">
              <tr>{["Date", "Provider", "Action", "Credits"].map((h) => <th key={h} className="text-left py-2 font-normal">{h}</th>)}</tr>
            </thead>
            <tbody className="text-[var(--pl-text)]">
              {rows.map((r, i) => (
                <tr key={i} className="border-t border-[var(--pl-border)]">
                  <td className="py-2.5 font-mono text-xs">{r[0]}</td>
                  <td className="font-mono text-xs">{r[1]}</td>
                  <td>{r[2]}</td>
                  <td className="text-[var(--pl-aqua)] font-mono">{r[3]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-2xl p-4 border border-[var(--pl-border)] bg-[var(--pl-surface)]/60">
      <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-text-muted)]">{label}</div>
      <div className="font-display text-2xl font-semibold mt-1 text-[var(--pl-text)]">{value}</div>
    </div>
  );
}

// --- 5. Promo Code ---
export function PromoCode() {
  const [code, setCode] = useState("");
  const [applied, setApplied] = useState(false);
  return (
    <div data-testid="settings-promo" className="space-y-6">
      <Card eyebrow="Apply a promo code">
        <div className="mt-2 flex flex-col sm:flex-row gap-2">
          <Input testId="promo-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="PREMIER25" />
          <button
            data-testid="promo-apply"
            onClick={() => code && setApplied(true)}
            className="h-11 px-4 rounded-xl pl-btn-aqua text-sm"
          >
            Apply
          </button>
        </div>
        {applied && (
          <div className="mt-3 text-xs text-emerald-300 flex items-center gap-1.5">
            <Check className="h-3.5 w-3.5" /> Code applied. 25% off your next renewal.
          </div>
        )}
      </Card>
      <Card eyebrow="Active discounts">
        <div className="text-sm text-[var(--pl-text-muted)]">No active discounts.</div>
      </Card>
      <Card eyebrow="Promo history">
        <div className="text-sm text-[var(--pl-text-muted)]">No prior codes redeemed.</div>
      </Card>
    </div>
  );
}

// --- 6. Affiliate Portal ---
export function Affiliate() {
  const [tab, setTab] = useState("dashboard");
  const tabs = [
    { id: "dashboard", label: "Dashboard" },
    { id: "referrals", label: "Referrals" },
    { id: "subs", label: "Sub affiliates" },
    { id: "commissions", label: "Commissions" },
    { id: "payouts", label: "Payouts" },
    { id: "assets", label: "Assets" },
    { id: "reports", label: "Reports" },
  ];
  const link = "https://premierlab.ai/?ref=founder";
  return (
    <div data-testid="settings-affiliate" className="space-y-6">
      <div className="flex flex-wrap gap-1.5">
        {tabs.map((t) => (
          <button
            key={t.id}
            data-testid={`aff-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={cn(
              "px-3 h-9 rounded-full text-xs border transition",
              tab === t.id
                ? "bg-[var(--pl-aqua)]/15 border-[var(--pl-aqua)]/40 text-[var(--pl-aqua)]"
                : "border-[var(--pl-border)] text-[var(--pl-text-muted)] hover:text-[var(--pl-text)]"
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "dashboard" && (
        <div className="space-y-4">
          <div className="rounded-2xl p-5 border border-[var(--pl-border)] bg-[var(--pl-surface)]/60">
            <Eyebrow>Your referral link</Eyebrow>
            <div className="mt-2 flex flex-col sm:flex-row gap-2">
              <Input testId="aff-link" readOnly value={link} />
              <button
                data-testid="aff-copy-btn"
                onClick={() => navigator.clipboard?.writeText(link)}
                className="h-11 px-4 rounded-xl pl-btn-aqua text-sm inline-flex items-center gap-2"
              >
                <Copy className="h-4 w-4" /> Copy link
              </button>
              <button data-testid="aff-customize" className="h-11 px-3 rounded-xl border border-[var(--pl-border)] text-xs text-[var(--pl-text)] hover:border-[var(--pl-aqua)]/40">
                Customize token
              </button>
              <button data-testid="aff-new" className="h-11 px-3 rounded-xl border border-[var(--pl-border)] text-xs text-[var(--pl-text)] hover:border-[var(--pl-aqua)]/40 inline-flex items-center gap-1.5">
                <Plus className="h-3.5 w-3.5" /> New link
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Clicks" value="2,184" />
            <Stat label="Referrals" value="48" />
            <Stat label="Customers" value="12" />
            <Stat label="Commissions" value="$1,820" />
          </div>
          <Card eyebrow="Share">
            <div className="flex flex-wrap gap-2 mt-2">
              {["X / Twitter", "LinkedIn", "Email", "Telegram", "Facebook"].map((s) => (
                <button key={s} data-testid={`aff-share-${s}`} className="h-9 px-3 rounded-xl border border-[var(--pl-border)] text-xs text-[var(--pl-text)] hover:border-[var(--pl-aqua)]/40">
                  {s}
                </button>
              ))}
            </div>
          </Card>
        </div>
      )}

      {tab === "referrals" && <Card eyebrow="Referrals">No referrals yet. Share your link to start earning.</Card>}
      {tab === "subs" && <Card eyebrow="Sub affiliates">Invite sub-affiliates and earn override commissions.</Card>}
      {tab === "commissions" && <Card eyebrow="Commissions">Lifetime: $1,820 · Pending: $240</Card>}
      {tab === "payouts" && <Card eyebrow="Payouts">Next payout: March 5 · $480 via Stripe</Card>}
      {tab === "assets" && (
        <Card eyebrow="Affiliate assets">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2">
            {["Banner Pack", "Email Swipe", "Demo Video"].map((a) => (
              <div key={a} data-testid={`aff-asset-${a}`} className="rounded-xl p-4 border border-[var(--pl-border)] bg-white/[0.03]">
                <div className="text-sm font-medium text-[var(--pl-text)]">{a}</div>
                <div className="text-[11px] text-[var(--pl-text-muted)] mt-0.5">Ready to share</div>
              </div>
            ))}
          </div>
        </Card>
      )}
      {tab === "reports" && <Card eyebrow="Reports">Detailed performance reports by month and campaign.</Card>}
    </div>
  );
}

// --- 7. API Dashboard (reuses component) ---
export function ApiDashboardPage() {
  return (
    <div data-testid="settings-api">
      <ApiDashboard scope="settings" />
    </div>
  );
}

// --- 8. Webhooks ---
export function Webhooks() {
  return (
    <div data-testid="settings-webhooks" className="space-y-6">
      <Card eyebrow="Add webhook endpoint">
        <div className="mt-2 flex flex-col sm:flex-row gap-2">
          <Input testId="wh-url" placeholder="https://example.com/hooks/premierlab" />
          <button data-testid="wh-verify" className="h-11 px-4 rounded-xl pl-btn-aqua text-sm inline-flex items-center gap-2">
            <Check className="h-4 w-4" /> Verify
          </button>
        </div>
        <div className="text-[11px] text-[var(--pl-text-muted)] mt-2">
          We send a signed POST. Endpoint must return 200 within 5s.
        </div>
      </Card>

      <Card eyebrow="Registered webhooks">
        <div className="overflow-x-auto mt-2">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-[0.18em] text-[var(--pl-text-muted)]">
              <tr>{["Name", "URL", "Status", "Last delivery", ""].map((h) => <th key={h} className="text-left py-2 font-normal">{h}</th>)}</tr>
            </thead>
            <tbody className="text-[var(--pl-text)]">
              {SAMPLE_WEBHOOKS.map((w) => (
                <tr key={w.id} data-testid={`wh-row-${w.id}`} className="border-t border-[var(--pl-border)]">
                  <td className="py-2.5">{w.name}</td>
                  <td className="font-mono text-xs text-[var(--pl-text-muted)] max-w-[260px] truncate">{w.url}</td>
                  <td>
                    <span className={cn("text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-md border",
                      w.status === "verified" ? "border-emerald-500/40 text-emerald-300" : "border-[var(--pl-aqua)]/40 text-[var(--pl-aqua)]")}>
                      {w.status}
                    </span>
                  </td>
                  <td className="text-xs text-[var(--pl-text-muted)]">{w.last_delivery}</td>
                  <td>
                    <button data-testid={`wh-del-${w.id}`} className="h-8 w-8 grid place-items-center rounded-lg border border-[var(--pl-border)] text-[var(--pl-text-muted)] hover:text-rose-400">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card eyebrow="Recent events">
        <ul className="text-sm space-y-1.5 mt-2 text-[var(--pl-text-muted)]">
          <li>· project.published → /hooks/published · 200 OK · 2 min ago</li>
          <li>· export.complete → /hooks/export · 200 OK · 5 hours ago</li>
          <li>· automation.failed → /hooks/automation-error · pending · —</li>
        </ul>
      </Card>
    </div>
  );
}

// --- 9. Connected Apps (USER-level only) ---
export function ConnectedApps() {
  const userApps = [
    ...STORAGE_PUBLISHING.filter((s) => ["gdrive-s", "dropbox-s"].includes(s.id)),
    ...MARKETING_CRM, // user-level CRM/marketing
  ];
  return (
    <div data-testid="settings-connected" className="space-y-6">
      <Card eyebrow="Connected apps">
        <p className="text-sm text-[var(--pl-text-muted)] mt-1">
          Workspace-level connections only. Platform provider keys are not shown here.
        </p>
      </Card>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {userApps.map((app) => (
          <div key={app.id} data-testid={`capp-${app.id}`} className="rounded-2xl p-5 border border-[var(--pl-border)] bg-[var(--pl-surface)]/60 pl-lift">
            <div className="flex items-start justify-between">
              <div className="h-10 w-10 rounded-xl bg-[var(--pl-aqua)]/10 border border-[var(--pl-aqua)]/30 grid place-items-center">
                <Plug className="h-5 w-5 text-[var(--pl-aqua)]" />
              </div>
              <span className={cn("text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-md border",
                app.state === "connected" ? "border-emerald-500/40 text-emerald-300" : "border-[var(--pl-border)] text-[var(--pl-text-muted)]")}>
                {app.state === "connected" ? "Connected" : "Not connected"}
              </span>
            </div>
            <div className="font-medium text-[var(--pl-text)] mt-3">{app.name}</div>
            <div className="text-xs text-[var(--pl-text-muted)] mt-1">{app.desc}</div>
            <button data-testid={`capp-btn-${app.id}`} className={cn(
              "mt-4 h-9 px-3 rounded-xl text-xs",
              app.state === "connected" ? "border border-[var(--pl-border)] text-[var(--pl-text)]" : "pl-btn-aqua"
            )}>
              {app.state === "connected" ? "Manage" : "Connect"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// --- 10. Billing History ---
export function Billing() {
  const rows = [
    ["INV-2026-0203", "Feb 3, 2026", "Premier · Monthly", "$129.00", "Paid"],
    ["INV-2026-0103", "Jan 3, 2026", "Premier · Monthly", "$129.00", "Paid"],
    ["INV-2025-1203", "Dec 3, 2025", "Premier · Monthly", "$129.00", "Paid"],
  ];
  return (
    <div data-testid="settings-billing" className="space-y-6">
      <Card eyebrow="Invoices">
        <div className="overflow-x-auto mt-2">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-[0.18em] text-[var(--pl-text-muted)]">
              <tr>{["Invoice", "Date", "Description", "Amount", "Status"].map((h) => <th key={h} className="text-left py-2 font-normal">{h}</th>)}</tr>
            </thead>
            <tbody className="text-[var(--pl-text)]">
              {rows.map((r) => (
                <tr key={r[0]} className="border-t border-[var(--pl-border)]">
                  <td className="py-2.5 font-mono text-xs">{r[0]}</td>
                  <td className="text-xs text-[var(--pl-text-muted)]">{r[1]}</td>
                  <td>{r[2]}</td>
                  <td className="font-mono text-[var(--pl-aqua)]">{r[3]}</td>
                  <td><span className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-md border border-emerald-500/40 text-emerald-300">{r[4]}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// --- 11. Payment Methods ---
export function PaymentMethods() {
  return (
    <div data-testid="settings-payment" className="space-y-6">
      <Card eyebrow="Saved payment methods">
        <div className="mt-2 space-y-2">
          <div className="flex items-center gap-3 p-3 rounded-xl border border-[var(--pl-border)] bg-white/[0.03]">
            <CreditCard className="h-5 w-5 text-[var(--pl-aqua)]" />
            <div className="flex-1">
              <div className="text-sm text-[var(--pl-text)] font-mono">Visa ···· 4242</div>
              <div className="text-[11px] text-[var(--pl-text-muted)]">Expires 04/29 · Default</div>
            </div>
            <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-md border border-emerald-500/40 text-emerald-300">Default</span>
          </div>
        </div>
        <button data-testid="add-payment-btn" className="mt-4 h-10 px-3 rounded-xl pl-btn-aqua text-xs inline-flex items-center gap-1.5">
          <Plus className="h-3.5 w-3.5" /> Add payment method
        </button>
      </Card>
    </div>
  );
}

// --- 12. Workspace Profile ---
export function WorkspaceProfile() {
  return (
    <div data-testid="settings-workspace" className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card><Field label="Workspace name"><Input testId="ws-name" defaultValue="Personal Workspace" /></Field></Card>
        <Card><Field label="Workspace slug"><Input testId="ws-slug" defaultValue="personal" /></Field></Card>
        <Card><Field label="Default brand kit"><Input testId="ws-brand" defaultValue="PremierLab · Aqua" /></Field></Card>
        <Card><Field label="Time zone"><Input testId="ws-tz" defaultValue="America/New_York (UTC-05)" /></Field></Card>
      </div>
      <div className="flex justify-end">
        <button data-testid="ws-save-btn" className="h-11 px-5 rounded-xl pl-btn-aqua text-sm">Save workspace</button>
      </div>
    </div>
  );
}

// --- 13. Team Members ---
export function Team() {
  const members = [
    { name: "Founder", email: "founder@premierlab.ai", role: "Owner" },
  ];
  return (
    <div data-testid="settings-team" className="space-y-6">
      <Card eyebrow="Members" action={
        <button data-testid="team-invite-btn" className="h-9 px-3 rounded-xl pl-btn-aqua text-xs inline-flex items-center gap-1.5"><Plus className="h-3.5 w-3.5" /> Invite</button>
      }>
        <div className="overflow-x-auto mt-2">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-[0.18em] text-[var(--pl-text-muted)]">
              <tr>{["Name", "Email", "Role"].map((h) => <th key={h} className="text-left py-2 font-normal">{h}</th>)}</tr>
            </thead>
            <tbody className="text-[var(--pl-text)]">
              {members.map((m) => (
                <tr key={m.email} className="border-t border-[var(--pl-border)]">
                  <td className="py-2.5">{m.name}</td>
                  <td className="text-xs text-[var(--pl-text-muted)]">{m.email}</td>
                  <td><span className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-md border border-[var(--pl-aqua)]/40 text-[var(--pl-aqua)]">{m.role}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      <Card eyebrow="Pending invites">
        <div className="text-sm text-[var(--pl-text-muted)]">No pending invites.</div>
      </Card>
    </div>
  );
}

// --- 14. Brand Defaults ---
export function BrandDefaults() {
  return (
    <div data-testid="settings-brand" className="space-y-6">
      <Card eyebrow="Default brand kit">
        <div className="mt-2 flex items-center gap-3">
          <div className="h-12 w-12 rounded-xl pl-signature-gradient" />
          <div className="flex-1">
            <div className="text-sm text-[var(--pl-text)]">PremierLab · Aqua</div>
            <div className="text-[11px] text-[var(--pl-text-muted)]">Set as default for all new projects</div>
          </div>
          <button data-testid="brand-change-btn" className="h-9 px-3 rounded-xl border border-[var(--pl-border)] text-xs text-[var(--pl-text)] hover:border-[var(--pl-aqua)]/40">Change</button>
        </div>
      </Card>
      <Card eyebrow="Defaults">
        <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Default font"><Input testId="brand-font" defaultValue="Outfit" /></Field>
          <Field label="Default body color"><Input testId="brand-color" defaultValue="#F4FFFC" /></Field>
        </div>
      </Card>
    </div>
  );
}

// --- 15. Storage ---
export function Storage() {
  return (
    <div data-testid="settings-storage" className="space-y-6">
      <Card eyebrow="Workspace storage">
        <div className="mt-2 flex items-baseline gap-2">
          <div className="font-display text-2xl font-semibold text-[var(--pl-text)]">4.2 GB</div>
          <div className="text-xs text-[var(--pl-text-muted)]">of 50 GB used</div>
        </div>
        <div className="mt-3 h-1.5 rounded-full bg-white/5 overflow-hidden">
          <div className="h-full pl-signature-gradient" style={{ width: "8.4%" }} />
        </div>
      </Card>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Images" value="1.1 GB" />
        <Stat label="PDFs" value="780 MB" />
        <Stat label="Audio" value="1.4 GB" />
        <Stat label="Videos" value="920 MB" />
      </div>
    </div>
  );
}

// --- 16. Privacy ---
export function Privacy() {
  const { role, setRole, isAdmin } = useRole();
  return (
    <div data-testid="settings-privacy" className="space-y-6">
      <Card eyebrow="Data & privacy">
        <ul className="mt-2 space-y-1.5 text-sm text-[var(--pl-text)]">
          <li>· Your generations are private to your workspace by default.</li>
          <li>· You can request export or deletion of your data at any time.</li>
          <li>· We never train models on your private data.</li>
        </ul>
      </Card>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <button data-testid="privacy-export-btn" className="h-11 px-4 rounded-xl border border-[var(--pl-border)] text-sm text-[var(--pl-text)] hover:border-[var(--pl-aqua)]/40 text-left">
          <div className="font-medium">Export my data</div>
          <div className="text-xs text-[var(--pl-text-muted)] mt-0.5">Receive a downloadable archive within 24h.</div>
        </button>
        <button data-testid="privacy-delete-btn" className="h-11 px-4 rounded-xl border border-rose-500/40 text-sm text-rose-300 hover:bg-rose-500/5 text-left">
          <div className="font-medium">Delete workspace</div>
          <div className="text-xs text-rose-300/70 mt-0.5">Permanent. Cannot be undone.</div>
        </button>
      </div>

      <Card eyebrow="Dev · Role switcher" action={
        <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-md border border-[var(--pl-border)] text-[var(--pl-text-muted)]">demo</span>
      }>
        <p className="text-xs text-[var(--pl-text-muted)] mt-1">
          Demo only — in production this is set by the auth provider. Current role:{" "}
          <span className="font-mono text-[var(--pl-aqua)]">{role}</span>
          {isAdmin && <span className="ml-2 text-emerald-300">(admin access enabled)</span>}
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          {["user", "platform_admin", "super_admin"].map((r) => (
            <button
              key={r}
              data-testid={`role-${r}`}
              onClick={() => setRole(r)}
              className={cn(
                "h-9 px-3 rounded-xl text-xs border transition",
                role === r
                  ? "bg-[var(--pl-aqua)]/15 border-[var(--pl-aqua)]/40 text-[var(--pl-aqua)]"
                  : "border-[var(--pl-border)] text-[var(--pl-text)] hover:border-[var(--pl-aqua)]/40"
              )}
            >
              {r}
            </button>
          ))}
        </div>
      </Card>
    </div>
  );
}

// --- 17. Notifications ---
export function Notifications() {
  const items = [
    ["Project exports complete", true],
    ["Automation failures", true],
    ["Team activity digest (weekly)", false],
    ["Product updates and feature drops", true],
    ["Billing & credit warnings", true],
  ];
  return (
    <div data-testid="settings-notifications" className="space-y-6">
      <Card eyebrow="Email notifications">
        <ul className="mt-2 divide-y divide-[var(--pl-border)]">
          {items.map(([label, on], i) => (
            <li key={label} data-testid={`notif-${i}`} className="py-3 flex items-center justify-between">
              <span className="text-sm text-[var(--pl-text)]">{label}</span>
              <button className={cn(
                "h-6 w-11 rounded-full transition relative",
                on ? "bg-[var(--pl-aqua)]" : "bg-white/10"
              )}>
                <span className={cn(
                  "absolute top-0.5 h-5 w-5 rounded-full bg-white transition",
                  on ? "right-0.5" : "left-0.5"
                )} />
              </button>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}

// --- 18. Support ---
export function Support() {
  const items = [
    { icon: LifeBuoy, label: "Help Center", desc: "Guides, tutorials, and best practices.", cta: "Open" },
    { icon: MessageCircle, label: "Join Community", desc: "Discord, peer support, founder office hours.", cta: "Join" },
    { icon: Mail, label: "Contact Support", desc: "Reach our team. Average response: 4h.", cta: "Email" },
  ];
  return (
    <div data-testid="settings-support" className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {items.map((i) => {
          const I = i.icon;
          return (
            <div key={i.label} data-testid={`support-${i.label}`} className="rounded-2xl p-5 border border-[var(--pl-border)] bg-[var(--pl-surface)]/60 pl-lift">
              <div className="h-10 w-10 rounded-xl bg-[var(--pl-aqua)]/10 border border-[var(--pl-aqua)]/30 grid place-items-center"><I className="h-5 w-5 text-[var(--pl-aqua)]" /></div>
              <div className="font-medium text-[var(--pl-text)] mt-3">{i.label}</div>
              <div className="text-xs text-[var(--pl-text-muted)] mt-1">{i.desc}</div>
              <button data-testid={`support-cta-${i.label}`} className="mt-4 h-9 px-3 rounded-xl pl-btn-aqua text-xs">{i.cta}</button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
